import React, { Fragment } from "react";
import {
  InputLabel,
  FormControl,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
  Select,
} from "@mui/material";
import { CurrencyWithPrice } from "../CurrencyWithPrice";
import { colors } from "../../theme";
import { Price } from "./types";
import { IVerticalPricingCalculatorTranslations } from "../../types";
import { lighten } from "@mui/material/styles";

export type CustomQuantityMenuItemProps = {
  prices: Price[];
  customQuantity: boolean;
  locale: string;
  handleCustomPricing: (val: Price) => void;
  pricesIncTax: boolean;
  quantity: number;
  setCustomQuantity: (val: boolean) => void;
  variant: "desktop" | "mobile";

  translations: {
    incTax: string;
    exTax: string;
    save: string;
    quantityTitle: string;
    customQuantityLabel: string;
  };
};

const CustomQuantityMenuItem: React.FC<CustomQuantityMenuItemProps> = (
  props: CustomQuantityMenuItemProps
) => {
  const {
    prices,
    customQuantity,
    locale,
    quantity,
    handleCustomPricing,
    pricesIncTax,
    setCustomQuantity,
    variant,
  } = props;

  if (variant === "desktop") {
    return (
      <RadioGroup value={quantity}>
        {prices.map((price) => {
          const selected = !customQuantity && price.quantity === quantity;

          return (
            <Grid
              container
              key={price.quantity}
              alignItems="center"
              sx={{
                backgroundColor: selected ? colors.coolBlueLight : "inherit",
                borderRadius: "9px",
                height: "44px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: lighten(colors.coolBlueLight, 0.6),
                },
              }}
              onClick={() => handleCustomPricing(price)}
            >
              <Grid item xs={2} sx={{ px: "12px" }}>
                <Radio
                  color="default"
                  checked={selected}
                  value={price.quantity}
                  sx={{ transform: "scale(1.5)" }}
                  disabled={price.quantity < price.moq}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: {
                      xs: "14px",
                      md: "16px",
                    },
                  }}
                >
                  {price.quantity}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: {
                      xs: "14px",
                      md: "16px",
                    },
                  }}
                >
                  <CurrencyWithPrice
                    locale={locale}
                    price={
                      price.quantity < price.moq
                        ? null
                        : pricesIncTax
                        ? price.priceIncTax
                        : price.priceExTax
                    }
                    currencyCode={price.currencyCode}
                  />
                </Typography>
              </Grid>
              <Grid item xs={4}>
                {selected ? (
                  <Typography
                    variant="body1"
                    sx={{ color: colors.grey, fontSize: 12 }}
                  >
                    {pricesIncTax
                      ? props.translations.incTax
                      : props.translations.exTax}
                  </Typography>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: colors.green, fontSize: 14 }}
                  >
                    {props.translations.save}{" "}
                    {(price.totalDiscountPct * 100).toFixed(0)}%
                  </Typography>
                )}
              </Grid>
            </Grid>
          );
        })}
        <Grid
          container
          alignItems="center"
          sx={{
            backgroundColor: customQuantity ? colors.coolBlueLight : "inherit",
            cursor: "pointer",
            borderRadius: "9px",
            height: "44px",
            "&:hover": {
              backgroundColor: lighten(colors.coolBlueLight, 0.25),
            },
          }}
          onClick={() => {
            setCustomQuantity(true);
          }}
        >
          <Grid item xs={2} sx={{ px: "12px" }}>
            <Radio
              color="default"
              checked={customQuantity}
              sx={{ transform: "scale(1.5)" }}
            />
          </Grid>
          <Grid item xs={10}>
            <Typography
              variant="body1"
              sx={{
                fontSize: {
                  xs: "14px",
                  md: "16px",
                },
              }}
            >
              {props.translations.quantityTitle}
            </Typography>
          </Grid>
        </Grid>
      </RadioGroup>
    );
  }

  return (
    <FormControl fullWidth>
      <InputLabel id="quantity-custom-select-label">
        {props.translations.customQuantityLabel}
      </InputLabel>
      <Select
        labelId="quantity-custom-select-label"
        id="quantity-custom-select"
        label={props.translations.customQuantityLabel}
        value={quantity}
      >
        {prices.map((price) => {
          const selected = !customQuantity && price.quantity === quantity;
          return (
            <MenuItem
              key={price.quantity}
              value={price.quantity}
              onClick={() => handleCustomPricing(price)}
              disabled={price.quantity < price.moq}
            >
              <Grid container key={price.quantity} alignItems="center">
                <Grid item xs={3}>
                  <Typography variant="body1">{price.quantity}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body1">
                    <CurrencyWithPrice
                      locale={locale}
                      price={
                        price.quantity < price.moq
                          ? null
                          : pricesIncTax
                          ? price.priceIncTax
                          : price.priceExTax
                      }
                      currencyCode={price.currencyCode}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  {selected ? (
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.grey,
                        fontSize: "14px",
                        textTransform: "capitalize",
                      }}
                    >
                      {pricesIncTax
                        ? props.translations.incTax
                        : props.translations.exTax}
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ color: colors.green }}>
                      {props.translations.save}{" "}
                      {(price.totalDiscountPct * 100).toFixed(0)}%
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </MenuItem>
          );
        })}
        <MenuItem value={customQuantity ? quantity : ""}>
          <Grid
            container
            alignItems="center"
            onClick={() => setCustomQuantity(true)}
          >
            <Grid item xs={10}>
              <Typography variant="body1">
                {props.translations.quantityTitle}
              </Typography>
            </Grid>
          </Grid>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default CustomQuantityMenuItem;
